import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface ChannelRow {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly country: string | null;
  readonly language: string;
  readonly rssUrl: string;
  readonly isActive: boolean;
  readonly qualityScore: number | null;
  readonly articleCount: number | null;
  readonly avgArticlesPerDay: number | null;
  readonly consecutiveFailures: number;
  readonly lastFetchedAt: string | null;
  readonly lastArticleAt: string | null;
}

const fmtInt = new Intl.NumberFormat('en-US');

const columns: readonly Column<ChannelRow>[] = [
  {
    key: 'name',
    header: 'Channel',
    render: (r) => (
      <a
        href={r.rssUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--text-primary)', fontWeight: 500, textDecoration: 'none' }}
        title={r.rssUrl}
      >
        {r.name}
      </a>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    width: '140px',
    render: (r) => <span className="nw-chip">{r.category}</span>,
  },
  {
    key: 'country',
    header: 'Country',
    width: '90px',
    render: (r) => r.country ?? '—',
  },
  {
    key: 'language',
    header: 'Lang',
    width: '70px',
    render: (r) => <span className="nw-chip">{r.language}</span>,
  },
  {
    key: 'isActive',
    header: 'Active',
    width: '90px',
    render: (r) =>
      r.isActive ? (
        <span className="nw-chip nw-chip--success">on</span>
      ) : (
        <span className="nw-chip nw-chip--error">off</span>
      ),
  },
  {
    key: 'articleCount',
    header: 'Articles',
    width: '100px',
    align: 'right',
    render: (r) => (r.articleCount === null ? '—' : fmtInt.format(r.articleCount)),
  },
  {
    key: 'avgArticlesPerDay',
    header: 'Per day',
    width: '90px',
    align: 'right',
    render: (r) =>
      r.avgArticlesPerDay === null ? '—' : r.avgArticlesPerDay.toFixed(1),
  },
  {
    key: 'consecutiveFailures',
    header: 'Fails',
    width: '80px',
    align: 'right',
    render: (r) =>
      r.consecutiveFailures > 0 ? (
        <span style={{ color: 'var(--error)', fontWeight: 600 }}>
          {r.consecutiveFailures}
        </span>
      ) : (
        '0'
      ),
  },
  {
    key: 'lastArticleAt',
    header: 'Last article',
    width: '140px',
    render: (r) =>
      r.lastArticleAt ? (
        <span title={r.lastArticleAt} style={{ color: 'var(--text-secondary)' }}>
          {formatDistanceToNow(new Date(r.lastArticleAt), { addSuffix: true })}
        </span>
      ) : (
        '—'
      ),
  },
];

export function ChannelsList() {
  const query = useQuery({
    queryKey: queryKeys.channels.list(),
    queryFn: async () => {
      const res = await apiClient.get<readonly ChannelRow[]>('/owner/channels');
      return res.data;
    },
  });

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Channels</h1>
        <p className="nw-page-header__subtitle">
          Every RSS source NewsWave ingests. Quality + freshness at a glance — failures roll up here first.
        </p>
      </header>
      <DataTable
        columns={columns}
        rows={query.data ?? []}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        error={query.error ? (query.error as Error).message : null}
        emptyMessage="No channels yet."
      />
    </>
  );
}
